import { Injectable } from '@angular/core';

import { User } from '@models/user';
import { Logger, LoggingService } from '@services/logging.service';

const FLAG_FALLBACK_STORAGE_PREFIX = 'ld-flag-fallback';

/**
 * Remembers the last LaunchDarkly flag value that was read for an identified user, so a
 * flag-gated guard can fall back to it (instead of a hard-coded default) when LaunchDarkly
 * is slow or fails on a fresh page load. The LaunchDarkly SDK clears its own persisted
 * flags on every identify(), so it cannot serve this purpose.
 *
 * Values are keyed by tenant and by the user's own identifier (`user_sub`, else `id`) so one
 * user's value is never served to another, even within a tenant or on a shared browser.
 * A user with neither identifier is not cached.
 */
@Injectable({ providedIn: 'root' })
export class FlagFallbackCacheService {
  private logger: Logger;

  constructor(loggingService: LoggingService) {
    this.logger = loggingService.rootLogger.newLogger('FlagFallbackCacheService');
  }

  get(user: User | undefined, flagKey: string): boolean | undefined {
    const storageKey = this._buildStorageKey(user, flagKey);
    if (!storageKey) {
      return undefined;
    }
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === null ? undefined : stored === 'true';
    } catch (error) {
      this._logStorageFailure('Could not read cached flag value', flagKey, error);
      return undefined;
    }
  }

  set(user: User | undefined, flagKey: string, value: boolean): void {
    const storageKey = this._buildStorageKey(user, flagKey);
    if (!storageKey) {
      return;
    }
    try {
      localStorage.setItem(storageKey, String(value));
    } catch (error) {
      this._logStorageFailure('Could not store cached flag value', flagKey, error);
    }
  }

  private _buildStorageKey(user: User | undefined, flagKey: string): string | undefined {
    const userIdentifier = user?.user_sub || user?.id;
    if (!userIdentifier) {
      return undefined;
    }
    return [FLAG_FALLBACK_STORAGE_PREFIX, user.bank_id ?? '', userIdentifier, flagKey].join(':');
  }

  private _logStorageFailure(message: string, flagKey: string, error: unknown): void {
    this.logger.info(message, {
      flagKey,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
}
